import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Chip,
  Alert,
  Fade,
  useTheme
} from '@mui/material';
import {
  Save as SaveIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { ABCDEInput } from '../../../../services/abcdeAPI';
import RiskLevelBadge from './RiskLevelBadge';

interface ABCDEFormProps {
  formData: ABCDEInput;
  totalScore: number;
  loading: boolean;
  loadingDetails: boolean;
  submitSuccess: boolean;
  isUpdating: boolean;
  onInputChange: (field: keyof ABCDEInput, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const ABCDEForm: React.FC<ABCDEFormProps> = ({
  formData,
  totalScore,
  loading,
  loadingDetails,
  submitSuccess,
  isUpdating,
  onInputChange,
  onSubmit
}) => {
  const theme = useTheme();

  // ABCDE criteria descriptions
  const criteria = {
    asymmetry: {
      title: 'Asymmetry',
      description: 'One half of the mole does not match the other half',
      color: theme.palette.primary.main,
      options: [
        { value: 0, label: 'Symmetric', points: '0 points', description: 'Both halves match when folded' },
        { value: 1, label: 'Asymmetric in 1 axis', points: '1 point', description: 'Asymmetric in one direction' },
        { value: 2, label: 'Asymmetric in 2 axes', points: '2 points', description: 'Asymmetric in both directions' }
      ]
    },
    borders: {
      title: 'Borders',
      description: 'Edges are irregular, ragged, notched, or blurred',
      color: theme.palette.secondary.main,
      options: [
        { value: 0, label: 'Smooth, regular borders', points: '0 points', description: 'Clear, well-defined edges' },
        { value: 1, label: 'Irregular borders', points: '1 point', description: 'Ragged, notched, or blurred edges' }
      ]
    },
    color: {
      title: 'Color',
      description: 'Color varies from one area to another',
      color: theme.palette.warning.main,
      options: [
        { value: 0, label: 'Uniform color', points: '0 points', description: 'Single, consistent color throughout' },
        { value: 1, label: '2 colors', points: '1 point', description: 'Two distinct colors present' },
        { value: 2, label: '3+ colors', points: '2 points', description: 'Three or more colors present' }
      ]
    },
    diameter: {
      title: 'Diameter',
      description: 'Melanomas are usually larger than 6mm',
      color: theme.palette.info.main,
      options: [
        { value: 0, label: '< 6mm', points: '0 points', description: 'Smaller than a pencil eraser' },
        { value: 1, label: '≥ 6mm', points: '1 point', description: 'Larger than a pencil eraser' }
      ]
    },
    evolution: {
      title: 'Evolution',
      description: 'Any change in size, shape, color, elevation, or new symptoms',
      color: theme.palette.error.main,
      options: [
        { value: 0, label: 'No change', points: '0 points', description: 'Stable appearance over time' },
        { value: 1, label: 'Recent changes', points: '1 point', description: 'Any documented changes' }
      ]
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="primary" />
            Assessment Criteria
          </Typography>
          {isUpdating && (
            <Chip 
              label="Editing Mode" 
              color="info" 
              variant="outlined" 
              size="small" 
            />
          )}
        </Box>
        
        {loadingDetails && <LinearProgress sx={{ mb: 2 }} />}
        
        <form onSubmit={onSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Object.entries(criteria).map(([key, criterion]) => (
              <Paper key={key} elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ 
                    mb: 1, 
                    fontWeight: 600,
                    color: criterion.color,
                    fontSize: '1.1rem'
                  }}>
                    {criterion.title}
                  </FormLabel>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {criterion.description}
                  </Typography>
                  
                  <RadioGroup
                    value={formData[key as keyof ABCDEInput]}
                    onChange={(e) => onInputChange(key as keyof ABCDEInput, parseInt(e.target.value))}
                  >
                    {criterion.options.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio sx={{ color: criterion.color }} />}
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.label} ({option.points})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        }
                        sx={{ 
                          mb: 1,
                          '& .MuiFormControlLabel-label': { width: '100%' }
                        }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Paper>
            ))}

            {/* Score Display */}
            <RiskLevelBadge totalScore={totalScore} />

            {/* Notes and Recommendation */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Clinical Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => onInputChange('notes', e.target.value)}
                placeholder="Additional observations, clinical findings, or relevant patient history..."
                variant="outlined"
                fullWidth
              />
              
              <TextField
                label="Clinical Recommendation"
                multiline
                rows={3}
                value={formData.recommendation}
                onChange={(e) => onInputChange('recommendation', e.target.value)}
                placeholder="Recommended action based on assessment (e.g., monitor, biopsy, referral)..."
                variant="outlined"
                fullWidth
              />
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? undefined : <SaveIcon />}
              sx={{ mt: 2, py: 1.5 }}
            >
              {loading 
                ? (isUpdating ? 'Updating Assessment...' : 'Submitting Assessment...') 
                : (isUpdating ? 'Update ABCDE Assessment' : 'Submit ABCDE Assessment')
              }
            </Button>

            {submitSuccess && (
              <Fade in={submitSuccess}>
                <Alert severity="success" sx={{ mt: 2 }}>
                  Assessment submitted successfully!
                </Alert>
              </Fade>
            )}
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default ABCDEForm;
